import { Component, inject } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Crop, getCrops } from '@next/arable-lib/crop';
import { AbstractDialogResultData, AbstractFormDialog } from '@next/core-lib/dialog';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';

import { CropVariety, SiteState } from 'src/app/shared/models';

import { SalesPotentialDialogModel } from './sales-potential-dialog.model';
import { UpsertSalesPotentialRequest } from '../../actions/sales-potential.actions';

@Component({
  selector: 'app-sales-potential-dialog',
  templateUrl: './sales-potential-dialog.component.html',
  styleUrls: ['./sales-potential-dialog.component.scss'],
  standalone: false,
})
export class SalesPotentialDialogComponent extends AbstractFormDialog<UpsertSalesPotentialRequest> {
  data = inject<SalesPotentialDialogModel>(MAT_DIALOG_DATA);
  protected mdDialogRef =
    inject<
      MatDialogRef<
        SalesPotentialDialogComponent,
        AbstractDialogResultData<UpsertSalesPotentialRequest>
      >
    >(MatDialogRef);
  private readonly fb = inject(FormBuilder);
  private readonly store = inject<Store<SiteState>>(Store);

  crops$: Observable<Crop[]>;
  filteredCrops$: Observable<Crop[]>;

  formGroup = this.fb.group({
    id: [null as number | null],
    cropId: ['', Validators.required],
    areaInHectares: ['', [Validators.required, Validators.min(0)]],
    salesPotentialGbm: [false],
    salesPotentialBem: [false],
    salesPotentialSeed: [false],
    isSkal: [false],
    year: [this.data.year],
  });

  constructor() {
    super();

    this.crops$ = this.store.pipe(
      select(getCrops),
      map((crops) => [...crops].sort((a, b) => a.name.localeCompare(b.name))),
    );

    this.filteredCrops$ = this.formGroup.controls.cropId.valueChanges.pipe(
      startWith(''),
      filter((value) => typeof value === 'string'),
      map((value: string) =>
        this.removeDuplicateCrops([
          ...this.filter(value, [...this.data.mostUsedCrops]),
          ...this.filter(value, [...this.data.crops]),
        ]),
      ),
    );

    if (this.data.salesPotential) {
      this.formGroup.patchValue({
        id: this.data.salesPotential.id,
        cropId: this.data.salesPotential.cropId.toString(),
        areaInHectares: this.data.salesPotential.areaInHectares.toString(),
        salesPotentialGbm: this.data.salesPotential.salesPotentialGbm,
        salesPotentialBem: this.data.salesPotential.salesPotentialBem,
        salesPotentialSeed: this.data.salesPotential.salesPotentialSeed,
        isSkal: this.data.salesPotential.isSkal,
        year: this.data.year,
      });
    }
  }

  private removeDuplicateCrops(items: Crop[]): Crop[] {
    return items.filter(
      (v, i, a) => a.findIndex((t) => (!t?.id || !v?.id ? -1 : t.id === v.id)) === i,
    );
  }

  private filter(value: string, items: Crop[]): Crop[];
  private filter(value: string, items: CropVariety[]): CropVariety[];
  private filter(value: string, items: Crop[] | CropVariety[]) {
    return [...items]
      .sort((a, b) =>
        !(a.name && b.name) ? 0 : a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1,
      )
      .filter((option) =>
        !option.name ? false : option.name.toLowerCase().includes(value.toLowerCase()),
      );
  }

  displayCrop() {
    return (cropId?: number) => {
      return cropId
        ? this.data.crops.find((crop) => crop.id.toString() === cropId.toString())?.name
        : '';
    };
  }

  protected onSubmit(): UpsertSalesPotentialRequest {
    const formValue = this.formGroup.value;
    return {
      id: formValue.id || undefined,
      cropId: Number.parseInt(formValue.cropId, 10),
      areaInHectares: Number.parseFloat(formValue.areaInHectares),
      year: formValue.year,
      salesPotentialGbm: formValue.salesPotentialGbm,
      salesPotentialBem: formValue.salesPotentialBem,
      salesPotentialSeed: formValue.salesPotentialSeed,
      isSkal: formValue.isSkal,
    };
  }
}
